"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addHours, format, parse } from "date-fns";
import { useTranslation } from "react-i18next";
import { bookingService } from "@/lib/api/services/booking.service";
import type { Field, Slot } from "@/lib/types/booking";
import { validateArgentinaPhone } from "@/lib/validation/argentina-phone";
import { partialPaymentAmount } from "@/lib/booking/partial-payment";
import { createInquirySchema } from "./schema";
import type { InquiryFormData } from "./types";

/** "08:00" → "09:00" for matching API slot end times */
function timePlusOneHour(time: string): string {
  const d = parse(time, "HH:mm", new Date());
  return format(addHours(d, 1), "HH:mm");
}

export function useInquiryForm() {
  const { t } = useTranslation();
  const todayIso = format(new Date(), "yyyy-MM-dd");

  const schema = React.useMemo(
    () => createInquirySchema((key) => t(key)),
    [t]
  );

  const form = useForm<InquiryFormData>({
    resolver: zodResolver(schema),
    defaultValues: { fecha: todayIso },
  });

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = form;
  const fecha = watch("fecha");
  const horario = watch("horario");
  const horarioHasta = watch("horarioHasta");
  const cancha = watch("cancha");

  const [allFields, setAllFields] = React.useState<Field[]>([]);
  const [slotsByField, setSlotsByField] = React.useState<Map<number, Slot[]>>(new Map());
  const [loading, setLoading] = React.useState(false);
  const [showSummaryModal, setShowSummaryModal] = React.useState(false);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [summaryData, setSummaryData] = React.useState<InquiryFormData | null>(null);
  /** When true, user pays advance only. Default false = full payment; check to pay partial. */
  const [payPartial, setPayPartial] = React.useState(false);
  const [payAmount, setPayAmount] = React.useState(0);
  const [pricing, setPricing] = React.useState<{
    totalAmount: number;
    advanceAmount: number;
    remainingAmount: number;
    hours: number;
    pricePerHour: number;
  } | null>(null);

  React.useEffect(() => {
    if (!fecha) return;
    setValue("cancha", "");
    setValue("horario", "");
    setValue("horarioHasta", "");
    setPricing(null);
    setSlotsByField(new Map());
    bookingService.getFields(fecha).then(setAllFields).catch(() => setAllFields([]));
  }, [fecha, setValue]);

  React.useEffect(() => {
    if (!fecha || !cancha) return;
    setValue("horario", "");
    setValue("horarioHasta", "");
    setPricing(null);
    bookingService
      .getSlots(fecha, cancha)
      .then((slots) => {
        const map = new Map<number, Slot[]>();
        map.set(Number(cancha), slots);
        setSlotsByField(map);
      })
      .catch(() => setSlotsByField(new Map()));
  }, [fecha, cancha, setValue]);

  const rawSlots = React.useMemo(() => {
    return slotsByField.get(Number(cancha)) ?? [];
  }, [slotsByField, cancha]);

  // Only available slots (booked slots are completely filtered out)
  const slots = React.useMemo(() => {
    return rawSlots
      .filter((s) => s.available)
      .sort((a, b) => a.from.localeCompare(b.from));
  }, [rawSlots]);

  // Contiguous available end times starting from the selected 'horario'
  const availableToOptions = React.useMemo(() => {
    if (!cancha || !horario) return [];
    const toList: string[] = [];
    let currentFrom = horario;

    while (true) {
      const match = rawSlots.find((s) => s.from === currentFrom && s.available);
      if (!match) break;
      toList.push(match.to);
      currentFrom = match.to;
    }
    return toList;
  }, [rawSlots, cancha, horario]);

  const prevHorarioRef = React.useRef<string>("");

  React.useEffect(() => {
    if (!horario) {
      prevHorarioRef.current = "";
      setValue("horarioHasta", "");
      return;
    }
    if (!cancha || availableToOptions.length === 0) {
      setValue("horarioHasta", "");
      return;
    }
    if (prevHorarioRef.current === horario && availableToOptions.includes(horarioHasta)) {
      return;
    }
    prevHorarioRef.current = horario;

    const targetTo = timePlusOneHour(horario);
    const toValue = availableToOptions.includes(targetTo)
      ? targetTo
      : availableToOptions[0];

    setValue("horarioHasta", toValue, { shouldValidate: true, shouldDirty: true });
  }, [horario, cancha, availableToOptions, horarioHasta, setValue]);

  React.useEffect(() => {
    if (!horario || !horarioHasta || !cancha) return;
    bookingService
      .getPricing(cancha, horario, horarioHasta)
      .then((d) => {
        if (d) {
          setPricing(d);
          setPayPartial(false);
          setPayAmount(d.totalAmount);
          setValue("monto", d.totalAmount);
        }
      })
      .catch(() => setPricing(null));
  }, [horario, horarioHasta, cancha, setValue]);

  const onSubmit = React.useCallback((data: InquiryFormData) => {
    setSummaryData(data);
    setShowSummaryModal(true);
  }, []);

  const handleModalConfirm = React.useCallback(() => {
    if (!pricing) return;
    setShowSummaryModal(false);
    setPayPartial(false);
    setPayAmount(pricing.totalAmount);
    setShowPaymentModal(true);
  }, [pricing]);

  const startPayment = React.useCallback(async () => {
    if (!summaryData || !pricing) return;

    try {
      setLoading(true);
      const chargedAmount = payPartial
        ? partialPaymentAmount(pricing.totalAmount)
        : pricing.totalAmount;
      const { normalized } = validateArgentinaPhone(summaryData.telefono);
      const phoneForApi = normalized ? `54${normalized}` : summaryData.telefono;

      const reservation = await bookingService.createReservation({
        date: summaryData.fecha,
        timeFrom: summaryData.horario,
        timeTo: summaryData.horarioHasta,
        fieldId: Number(summaryData.cancha),
        areaCode: "",
        phone: phoneForApi,
        customerName: summaryData.nombre,
        amount: pricing.totalAmount,
        email: summaryData.email,
      });

      const accessToken =
        reservation.paymentAccessToken ?? (reservation as any).accessToken;
      if (typeof accessToken !== "string" || !accessToken.trim()) {
        throw new Error("Missing payment access token in reservation response");
      }

      const payment = payPartial
        ? await bookingService.createAdvancePayment(reservation.id, accessToken)
        : await bookingService.createPayment(reservation.id, accessToken);

      const fieldName =
        allFields.find((f) => String(f.fieldId) === summaryData.cancha)
          ?.fieldName ?? summaryData.cancha;
      sessionStorage.setItem(
        "paymentSuccessData",
        JSON.stringify({
          reservationId: reservation.id,
          paymentAccessToken: accessToken,
          fecha: summaryData.fecha,
          horario: summaryData.horario,
          horarioHasta: summaryData.horarioHasta,
          fieldName,
          paidAmount: chargedAmount,
          totalAmount: pricing.totalAmount,
          nombre: summaryData.nombre,
          telefono: summaryData.telefono,
        })
      );

      const initUrl =
        payment?.initPoint ||
        (payment as any)?.paymentUrl ||
        (payment as any)?.init_point ||
        (payment as any)?.sandbox_init_point ||
        (payment as any)?.url;

      if (initUrl) {
        if (!initUrl.startsWith("https://") && !initUrl.startsWith("http://")) {
          throw new Error("Invalid payment URL provided by server");
        }
        window.location.href = initUrl;
      } else {
        throw new Error("No payment init point returned");
      }
    } catch (err: any) {
      alert(err?.message || t("payment.error"));
    } finally {
      setLoading(false);
    }
  }, [summaryData, pricing, payPartial, allFields, t]);

  return {
    form,
    register,
    control,
    handleSubmit,
    setValue,
    errors,
    allFields,
    slots,
    availableToOptions,
    horario,
    horarioHasta,
    pricing,
    loading,
    showSummaryModal,
    showPaymentModal,
    summaryData,
    payPartial,
    payAmount,
    onSubmit,
    setShowSummaryModal,
    setShowPaymentModal,
    handleModalConfirm,
    startPayment,
    setPayPartial,
    setPayAmount,
  };
}
